/**
 * SearchReportExportMenu — the Search Detail "Export" dropdown.
 *
 * Purely presentational: it renders the four formats and reports the chosen one.
 * The fetch and the file-save live on the page, mirroring how `ImportOwnerMenu`
 * pairs with `ImportsPage` — a menu that also downloaded would be a component no
 * test could exercise without a DOM stub.
 *
 * A RADIX `DropdownMenu`, PORTALLED (2026-10-07), the way `ReportDownloadMenu`
 * is. In place, as an `absolute z-10` child of the toolbar, the panel opened
 * UNDER the Candidates table's sticky header cells (`sticky top-0 z-10` in
 * `CandidatesTable.tsx`, later in the DOM, so they won the tie) and hid its
 * second entry behind the headers. The portal on `document.body` at `z-50`
 * takes it out of every stacking context on the page. `DropdownMenu` rather
 * than a Popover (PR #1022 iter 1): it moves focus into the menu, roves the
 * items with the arrow keys, closes on Escape and outside press, and returns
 * focus to the trigger — the `role="menu"` contract, which the first portalled
 * version (a Popover with focus left on the trigger) broke for keyboard users.
 *
 * Disabled when there is nothing composed yet, NOT whenever the report is
 * refreshing. A stale report has real numbers on screen and the backend will
 * happily export them (the document states its own freshness), so greying the
 * button out during a refresh would refuse to export exactly what the user is
 * looking at.
 *
 * Design-system utilities only; no inline `style`. Copy in English.
 */

import type { SearchReportExportFormat } from "@nu-assist/contracts";
import { Spinner } from "@nu-assist/ui";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronDown, Download } from "lucide-react";

/**
 * What one menu entry downloads.
 *
 * `html-full` and `html-view` are NOT server formats — they are built in the
 * browser from the page's own components (see `export/buildReportDocument`).
 * They share this menu because a reader choosing how to take the report away
 * should not have to know which half of the system makes each file.
 */
export type ExportChoice = SearchReportExportFormat | "html-full" | "html-view";

interface FormatOption {
  format: ExportChoice;
  label: string;
  hint: string;
}

/** The tab the menu is pressed on. */
export type ExportMenuSection = "dashboard" | "progress" | "candidates";

/**
 * ONE REPORT PER TAB, so the entries name the tab they came from.
 *
 * The reader is on a page that already says which tab it is; a menu offering
 * "Full report" from any would be offering different documents under one name.
 * What "as shown" MEANS differs too — the date range and granularity on Search
 * Progress, the candidate filters and order on Candidates — so the hint does.
 *
 * The DASHBOARD has nothing to show "as": since the Candidates tab was released
 * (2026-10-07) its candidate filters live on that tab, so an "as shown" entry
 * there built the same document as the full one under a hint ("With the
 * filters you have applied") that was no longer true. It offers the full
 * document only. From Candidates, the full document is still the Dashboard
 * report — every panel and every candidate.
 */
function documentOptions(section: ExportMenuSection): readonly FormatOption[] {
  if (section === "progress") {
    // "Search Progress" matches the tab and the exported document's caption.
    return [
      {
        format: "html-full",
        label: "Search Progress (.html)",
        hint: "The whole window, by day — ignores the range you set",
      },
      {
        format: "html-view",
        label: "Search Progress, as shown (.html)",
        hint: "The date range and granularity you have set",
      },
    ];
  }
  const full: FormatOption = {
    format: "html-full",
    label: "Dashboard (.html)",
    hint: "Every panel and every candidate",
  };
  if (section === "dashboard") return [full];
  return [
    { ...full, hint: "Every panel and every candidate — ignores your filters" },
    {
      format: "html-view",
      label: "Candidates, as shown (.html)",
      hint: "The report with only the candidates your filters show, in the table's order",
    },
  ];
}

/**
 * Menu order, chosen by intent rather than alphabetically: the two
 * prompt-shaped formats first, because "formatted for AI consumption" is the
 * reason this menu exists (v38 prototype), then the spreadsheet and the
 * standalone document.
 */
const FORMAT_OPTIONS: readonly FormatOption[] = [
  { format: "txt", label: "Plain text (.txt)", hint: "Paste into an AI prompt" },
  { format: "md", label: "Markdown (.md)", hint: "Prompt or doc, with tables" },
  { format: "csv", label: "Spreadsheet (.csv)", hint: "One row per figure" },
];

export interface SearchReportExportMenuProps {
  /** True while no report has been composed — nothing to put in a file. */
  disabled?: boolean;
  /** Which tab the menu is on — decides the document entries. */
  section?: ExportMenuSection;
  /** The entry currently downloading, if any. */
  busyFormat?: ExportChoice | null;
  onSelect: (format: ExportChoice) => void;
}

export function SearchReportExportMenu({
  section = "dashboard",
  disabled = false,
  busyFormat = null,
  onSelect,
}: SearchReportExportMenuProps) {
  const busy = busyFormat !== null;

  const itemClass =
    "block w-full cursor-pointer px-3 py-1.5 text-left outline-none data-[highlighted]:bg-accent-muted";

  return (
    <DropdownMenu.Root>
      <div data-testid="report-export-menu">
        <DropdownMenu.Trigger asChild disabled={disabled || busy}>
          <button
            type="button"
            // Says WHY it is unavailable — a disabled button with no explanation
            // is the state users report as "the export is broken".
            title={
              disabled
                ? "Available once this report has finished building"
                : "Download this report"
            }
            data-testid="report-export-trigger"
            className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-label text-text hover:border-border-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <Download className="h-4 w-4 text-text-secondary" aria-hidden="true" />
            )}
            <span>Export</span>
            <ChevronDown className="h-4 w-4 text-text-secondary" aria-hidden="true" />
          </button>
        </DropdownMenu.Trigger>
      </div>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          data-testid="report-export-panel"
          className="z-50 min-w-56 rounded-md border border-border bg-surface py-1 shadow-md"
        >
          {/* No per-entry busy state: choosing closes the menu and the trigger is
              disabled while a download runs, so the menu can never be open AND
              busy at once. The trigger's spinner is where progress lives. */}
          {documentOptions(section).map((option) => (
            <DropdownMenu.Item
              key={option.format}
              data-testid={`report-export-${option.format}`}
              onSelect={() => onSelect(option.format)}
              className={itemClass}
            >
              <span className="block text-label text-text">{option.label}</span>
              <span className="block text-caption text-text-secondary">{option.hint}</span>
            </DropdownMenu.Item>
          ))}
          {/* Documents above, data below. Presentational, NOT a menu
              separator: a hairline between two groups that the keyboard never
              needs to land on. */}
          <div aria-hidden="true" className="my-1 border-border border-t" />
          {FORMAT_OPTIONS.map((option) => (
            <DropdownMenu.Item
              key={option.format}
              data-testid={`report-export-${option.format}`}
              onSelect={() => onSelect(option.format)}
              className={itemClass}
            >
              <span className="block text-label text-text">{option.label}</span>
              <span className="block text-caption text-text-secondary">{option.hint}</span>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
